/**
 * The line beside "Open the posting": which browser the fill needs, whether
 * this one has the extension, whether the posting's site is one JobCork
 * fills. Decided in fillHint.ts; this only draws it.
 */

import type { ExtensionStatus } from "./extension";
import { browserFrom, fillHint } from "./fillHint";

/** This browser, read once: Chrome (or built on it), or not. */
export const BROWSER = browserFrom(
  typeof navigator === "undefined" ? "" : navigator.userAgent,
  typeof navigator === "undefined" ? null : ((navigator as Navigator & { userAgentData?: { brands?: Array<{ brand: string }> } }).userAgentData?.brands ?? null),
);

export function FillHint({ applyUrl, ext, autoMode = "review" }: { applyUrl: string | null | undefined; ext: ExtensionStatus | null; autoMode?: "auto" | "review" }) {
  const hint = fillHint(applyUrl, ext, BROWSER, autoMode);
  if (!hint) return null;
  return (
    <p className={`fill-hint small ${hint.tone}`}>
      {hint.text}
      {hint.connect && <> <a className="step-link small" href="/#extension" target="_blank" rel="noreferrer noopener">Connect the extension</a></>}
    </p>
  );
}

/**
 * The dashboard's fourth step: the extension, in this browser. Done only
 * where it is connected -- which is never in Safari or Firefox, and that is
 * the point: the step then says which browser to open JobCork in.
 */
export function extensionStep(ext: ExtensionStatus | null, browser = BROWSER): { done: boolean; label: string; hint: string } {
  const label = "Add the JobCork extension to Chrome";
  if (browser === "other") return { done: false, label, hint: "Optional. The extension fills a form in your own browser for you to check, and it runs in Chrome — this browser is not Chrome. Without it, “Send with JobCork” on any match sends from the cloud after you approve what goes." };
  if (ext === null) return { done: false, label, hint: "Checking for it in this browser…" };
  if (!ext.installed) return { done: false, label, hint: "Optional. It fills the application form on the posting's own page for you to check, and records what it did. Install it, then open this page again — it connects itself. Without it, “Send with JobCork” on any match sends from the cloud." };
  if (!ext.connected) return { done: false, label, hint: "It is installed here and connecting…" };
  return { done: true, label, hint: `Connected in this browser${ext.version ? ` (v${ext.version})` : ""}. Open a posting from your matches and it offers to fill the form.` };
}
